/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type {CoreContext} from '../../models/Context';
import type {FeatureFlagConfig} from '@adobe/exc-app/featureflags';
import type {
  FeatureFlagsById,
  GetFlagsContext,
  GraphQLPayload
} from '../../models/FeatureFlags';
import FeatureFlagService from './FeatureFlagService';
import {
  FeatureFlagsResponse,
  FeatureMetadata,
  FloodgateFeatureFlagsResponse
} from '@exc/graphql/src/models/floodgate';
import {getFloodgateFeatureFlags} from '@exc/graphql/src/queries/floodgate';
import {PROVIDERS} from '@adobe/exc-app/featureflags';
import {VALID_CLIENT_IDS} from '../../utils/floodgate';

class FloodgateService extends FeatureFlagService {
  constructor() {
    super(PROVIDERS.FLOODGATE, VALID_CLIENT_IDS);
  }

  /**
   * Cleans up the floodgate features flags response.
   * @param {FloodgateFeatureFlagsResponse} data - Array of feature flag objects
   * @returns {FeatureFlagsById} - A flattened object of projects with feature flag properties
   */
  public flattenFlags(data: FeatureFlagsResponse[]): FeatureFlagsById {
    const flattenedFlags: FeatureFlagsById = {};
    data.forEach(({clientId, featureFlags}) => {
      flattenedFlags[clientId] = {};
      featureFlags.forEach(({features = [], featuresMeta = []}) => {
        features.forEach(f => flattenedFlags[clientId][f] = 'true');
        featuresMeta.forEach(({feature, meta}: FeatureMetadata): void => {
          flattenedFlags[clientId][feature] = meta;
        });
      });
    });
    return flattenedFlags;
  }

  /**
   * A wrapping function to support the bulk API with a list of client ids
   * as well as clean up and sort the returned object
   * @param {Array} clientIds - Array of strings of client IDs
   * @param {GetFlagsContext} context - Current context object that includes options we need
   * @returns {FeatureFlagsById} - An object of client ids with flattened feature flags
   */
  public async getFeatureFlags(clientIds: string[], context: GetFlagsContext, config?: FeatureFlagConfig): Promise<FeatureFlagsById | undefined> {
    const {imsOrg} = context;
    return this.getFlagData(
      getFloodgateFeatureFlags as (payload: GraphQLPayload) => Promise<FloodgateFeatureFlagsResponse>,
      clientIds,
      context,
      imsOrg,
      undefined,
      config
    );
  }

  /**
   * Formats the options payload for the GraphQL request.
   * @param {Array} clientIds - Client ids
   * @param {GetFlagsContext} context - Core Context
   * @returns {FloodgateInput} - An object of client ids with context data
   * @public
   */
  public getProviderPayload(clientIds: string[], context: GetFlagsContext): GraphQLPayload {
    const {imsOrg, sandbox} = context;
    return {
      addCtx: [{key: 'sandboxType', value: sandbox?.type || 'PRODUCTION'}],
      clientIds,
      imsOrg
    };
  }

  /**
   * Checks context data to see if the required data is missing.
   * @param {Array} clientIds - Client ids
   * @param {CoreContext} context - Core Context
   * @returns {boolean} False if context has all data needed, true otherwise.
   */
  isContextMissing(clientIds: string[], context: CoreContext): boolean {
    return this.isCoreContextMissing(context);
  }

  /**
   * Cleans up the response, caches and returns it.
   * @param {string} contextKey - Unique key for type of feature flag query
   * @param {FloodgateFeatureFlagsResponse} graphQLResponse - Bulk response from GraphQl
   * @param {string} clientIds - Requests for one project have an id, defaults to ''
   * @returns {FeatureFlagsById} - An object of ids with flattened feature flags
   * @public
   */
  public processSuccessResponse(
    contextKey: string,
    graphQLResponse: FloodgateFeatureFlagsResponse,
    clientIds: string[]
  ): FeatureFlagsById {
    // Shape the response data
    const {fgFeatureFlags} = graphQLResponse;
    const flattenedFlags: FeatureFlagsById = this.flattenFlags(fgFeatureFlags);
    this.updateCache(contextKey, flattenedFlags, clientIds);
    // Return the flags sorted by client/application id so we're handling similar object shapes
    return flattenedFlags;
  }
}

export default new FloodgateService();
